import {
  IsBoolean,
  IsDateString,
  IsIn,
  IsNumber,
  IsOptional,
  IsString,
  Max,
  Min,
} from 'class-validator';
import { FIELD_VALUE_SOURCES, type FieldValueSource } from '../../label/field-value-source';
import { OptionalStringTransform } from '../../validation/optional-string.transformer';
import { DEFROST_DECLARATIONS, type DefrostDeclaration } from './defrost-declaration.enum';
import { PREPARATION_KINDS, type PreparationKind } from './preparation-kind.enum';
import { STORAGE_CONDITIONS, type StorageCondition } from './storage-condition.enum';

/**
 * Legal-data fields for `CounterLabelData`, shared by both subjects (Recipe
 * or Ingredient). The subject id is a route param (e.g.
 * `PATCH /counter-label/recipe/:recipeId`), never part of this body — same
 * reason `tenantId` never appears here. Every field is optional because the
 * record fills in incrementally: a tenant confirms one field at a time
 * (plan.md decision 5), there is no single "create" moment with all data at
 * once. `priceUnit`/`price` are deliberately absent: they live on
 * `MenuItem` and never invalidate a confirmation (plan.md W3.1).
 */
export class UpsertCounterLabelDataDto {
  @IsOptional()
  @IsString()
  @OptionalStringTransform()
  legalName?: string;

  @IsOptional()
  @IsIn(FIELD_VALUE_SOURCES)
  legalNameSource?: FieldValueSource;

  @IsOptional()
  @IsIn(STORAGE_CONDITIONS)
  storageCondition?: StorageCondition | null;

  @IsOptional()
  @IsIn(FIELD_VALUE_SOURCES)
  storageConditionSource?: FieldValueSource | null;

  @IsOptional()
  @IsNumber()
  @Min(-100)
  @Max(100)
  storageTemperatureMinC?: number | null;

  @IsOptional()
  @IsNumber()
  @Min(-100)
  @Max(100)
  storageTemperatureMaxC?: number | null;

  @IsOptional()
  @IsString()
  @OptionalStringTransform()
  storageNote?: string;

  @IsOptional()
  @IsBoolean()
  isPerishable?: boolean;

  @IsOptional()
  @IsBoolean()
  isFreshPasta?: boolean;

  @IsOptional()
  @IsDateString()
  expiryDate?: string;

  @IsOptional()
  @IsBoolean()
  isBeverage?: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  alcoholByVolume?: number;

  @IsOptional()
  @IsBoolean()
  isGlazedFrozen?: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  glazePercent?: number;

  @IsOptional()
  @IsBoolean()
  printExpiryOptIn?: boolean;

  @IsOptional()
  @IsIn(PREPARATION_KINDS)
  preparationKind?: PreparationKind;

  /**
   * Two different roles depending on value and subject, both funneled
   * through `resolveDefrostState` — never trusted as-is:
   *
   * - `FREEZING_TECH_REQUIRED`/`DEFROST_NO_IMPACT` (the two Allegato VI
   *   punto 2 exemptions): an explicit human statement, honored for BOTH
   *   subjects. These depend on the production process, not on
   *   `fromFrozen`/`frozenAtOrigin` rows, so `resolveDefrostState` can only
   *   accept them, never derive them (plan.md "Decongelato, prevenzione").
   * - Any other value, for the INGREDIENT subject only: states the freezer
   *   provenance of a counter item that has no recipe rows to read it from.
   * - Any other value, for the RECIPE subject: IGNORED — the declaration is
   *   derived from `preparationKind` + the recipe's
   *   `fromFrozen`/`frozenAtOrigin` rows instead. Kept on the DTO rather
   *   than removed so a client that still sends one of those gets a stable
   *   200 instead of a validation error.
   */
  @IsOptional()
  @IsIn(DEFROST_DECLARATIONS)
  defrostDeclaration?: DefrostDeclaration;

  /**
   * Explicit human answer to the gray zone "assembled raw from defrosted
   * components": art. 19 D.Lgs 231/2017 is not clear-cut there, so
   * `resolveDefrostState` never turns the badge on by itself — it proposes
   * and waits for this flag. Transient (never persisted): re-sending an
   * unrelated PATCH without it cannot switch an already-lit badge off,
   * because a lit badge is never switched off by derivation.
   */
  @IsOptional()
  @IsBoolean()
  confirmDefrostGrayZone?: boolean;

  @IsOptional()
  @IsString()
  @OptionalStringTransform()
  defrostExceptionNote?: string;
}
